import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { clientMetadataService } from '@/services/clientMetadataService';
import { inactiveReason, type Finding } from './clientChecks';

interface MarkInactiveDialogProps {
  /** The problem the person pressed "Mark inactive" on. Null closes the dialog. */
  finding: Finding | null;
  onClose: () => void;
  /** Called once the tag is on, by this press or by somebody else's first. */
  onMarked: (clientName: string) => void;
}

/**
 * Asks before a client is marked inactive from a check. The reason starts as
 * the check's name, and the person can change it. Nothing is written until
 * the person presses the button.
 */
export function MarkInactiveDialog({ finding, onClose, onMarked }: MarkInactiveDialogProps) {
  // The client stays on screen while the dialog fades out.
  const [shown, setShown] = useState<Finding | null>(finding);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadySet, setAlreadySet] = useState(false);

  useEffect(() => {
    if (!finding) return;
    setShown(finding);
    setReason(inactiveReason(finding.check));
    setError(null);
    setAlreadySet(false);
  }, [finding]);

  const name = shown?.clientName ?? '';

  const confirm = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await clientMetadataService.setInactive(name, reason);
      onMarked(name);
      if (!result.changed) {
        // Somebody marked the client inactive first. Their tag stands.
        setAlreadySet(true);
        return;
      }
      onClose();
    } catch (e) {
      console.error(`Could not mark ${name} inactive`, e);
      setError(`Nothing was changed.${e instanceof Error ? ` ${e.message}` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={finding !== null} onHide={busy ? undefined : onClose} centered>
      <Form onSubmit={(e) => void confirm(e)}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Mark {name} inactive?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {shown && (
            <p>
              {shown.problem}
              {shown.detail ? ` ${shown.detail}` : ''}
            </p>
          )}
          <p className="client-checks__effect">
            {name} leaves the client pickers, the All Production Clients group, Home&apos;s counts and the Credits
            table. A saved group run skips it. Nothing changes in its workspace, and a deploy to it on its own still
            works.
          </p>
          <Form.Group controlId="client-checks-reason">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy || alreadySet}
              maxLength={300}
            />
            <Form.Text>Saved on the tag. Configs shows it.</Form.Text>
          </Form.Group>
          {alreadySet && (
            <Alert variant="info" className="mt-3 mb-0">
              Somebody marked {name} inactive first. Their tag stands, and your reason was not saved.
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={busy}>
            {alreadySet ? 'Close' : 'Cancel'}
          </Button>
          {!alreadySet && (
            <Button type="submit" variant="primary" disabled={busy || !name}>
              {busy && <Spinner animation="border" size="sm" className="me-2" />}
              Mark inactive
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default MarkInactiveDialog;
